import React, {useEffect, useRef} from 'react'
import styles from './ConfirmModalContent.module.scss'
import {WuButton} from '@npm-questionpro/wick-ui-lib'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../Translation/English/global'

interface IConfirmModalContentProps {
  title?: string
  description: string | React.ReactNode
  handleConfirm: () => void
  handleCancel: () => void
  isLoading: boolean
  setContent?: (content: React.ReactNode) => void
  /** Styles the confirm button as a destructive action. @default false */
  isDestructive?: boolean
}

export const ConfirmModalContent: React.FC<IConfirmModalContentProps> = ({
  title,
  description,
  handleConfirm,
  handleCancel,
  isLoading,
  setContent,
  isDestructive = false,
}) => {
  const wt = useWt()

  // handleConfirm/handleCancel/wt are all fresh references on every parent
  // render (wt in particular is a brand-new closure on every call, not just
  // when translations change). Reading them via ref — instead of depending on
  // them below — keeps the effect from re-firing every render: depending on
  // their identity would call the parent's setContent on every render, which
  // re-renders the parent with new callbacks, which re-fires the effect —
  // an infinite loop.
  const latest = useRef({handleConfirm, handleCancel, wt})
  latest.current = {handleConfirm, handleCancel, wt}

  useEffect(() => {
    if (setContent) {
      setContent(
        <div className={styles.confirmModalContentButtons}>
          <WuButton
            variant="secondary"
            onClick={() => latest.current.handleCancel()}
          >
            {latest.current.wt(GLOBAL.cancel)}
          </WuButton>
          <WuButton
            variant="primary"
            color={isDestructive ? 'error' : 'primary'}
            onClick={() => latest.current.handleConfirm()}
            loading={isLoading}
          >
            {latest.current.wt(GLOBAL.confirm)}
          </WuButton>
        </div>,
      )
    }
  }, [setContent, isLoading, isDestructive])

  return (
    <div className={styles.confirmModalContent}>
      {title && title?.length > 0 && (
        <h1 className={styles.confirmModalContentTitle}>{title}</h1>
      )}
      <p className={styles.confirmModalContentDesc}>{description}</p>
    </div>
  )
}
